import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatDistanceToNow } from "date-fns";
import { mediaUrl, decodeText } from "@/lib/api";
import api from "@/lib/api";
import ReactionBar from "@/components/chat/ReactionBar";
import { useRealtime } from "@/context/RealtimeContext";

function clockTime(iso) { try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ""; } }

export default function PublicMessage({ m, mine, grouped, isStaff, onDelete, onReport, onModerate, onReply, onJumpToReply, onEdit, onMention, currentUsername }) {
  const navigate = useNavigate();
  const { subscribe } = useRealtime();
  const deleted = !!m.deleted;
  const [reaction, setReaction] = useState(m.reaction || { counts: {}, mine: null });
  const [active, setActive] = useState(false);

  useEffect(() => subscribe((evt) => {
    if (evt.type === "reaction" && evt.scope === "public" && evt.message_id === m.id) setReaction(evt.reaction);
  }), [subscribe, m.id]);

  const activate = (e) => {
    if (deleted) return;
    setActive((v) => !v);
    window.dispatchEvent(new CustomEvent("yaprio:message-action", { detail: { id: m.id, row: e.currentTarget } }));
  };

  const usernameColorStyle = { pink: "#f472b6", purple: "#c084fc", blue: "#60a5fa", green: "#34d399", orange: "#fb923c" }[m.username_color];
  const roleBadge = {
    vip: { label: "VIP", icon: "👑", className: "border-amber-200 bg-amber-50 text-amber-700" },
    elite: { label: "ELITE", icon: "💎", className: "border-sky-200 bg-sky-50 text-sky-700" },
    legend: { label: "LEGEND", icon: "🔥", className: "border-orange-200 bg-orange-50 text-orange-700" },
  }[String(m.yaprio_role || "").toLowerCase()];

  const renderText = (value) => String(value || "").split(/(@[A-Za-z0-9_]{2,32})/g).map((part, i) => { const isMention = /^@[A-Za-z0-9_]{2,32}$/.test(part); const isForMe = isMention && currentUsername && part.slice(1).toLowerCase() === currentUsername.toLowerCase(); return isForMe ? <span key={i} className="rounded bg-amber-200/80 px-1 font-bold text-amber-950 ring-1 ring-amber-300/70">{part}</span> : <React.Fragment key={i}>{part}</React.Fragment>; });

  const profileButton = (
    <button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/u/${m.username}`); }} disabled={deleted} className="shrink-0 rounded-full" aria-label={`Open @${m.username} profile`}>
      <span data-ring-style={m.profile_ring_style || "aurora"} style={{ "--ring-color": m.profile_ring_color || "#f97316" }} className={`yaprio-avatar-ring rounded-full ${m.profile_ring_animated ? `ring-${m.profile_ring_style || "aurora"}` : m.profile_ring_color ? "ring-solid" : ""}`}>
      <Avatar className="h-9 w-9 ring-2 ring-white dark:ring-slate-950">
        <AvatarImage src={mediaUrl(m.avatar_url)} alt={m.username} />
        <AvatarFallback className="bg-pink-500 text-sm font-bold text-white">{(m.username || "?").charAt(0).toUpperCase()}</AvatarFallback>
      </Avatar>
      </span>
    </button>
  );

  return (
    <div
      onClick={activate}
      className={`group relative flex w-full items-start gap-2.5 ${grouped ? "mt-0.5" : "mt-3"}`}
      data-testid="public-message"
      data-grouped={grouped ? "true" : "false"}
      data-deleted={deleted ? "true" : "false"}
      data-message-id={m.id}
    >
      <div className="w-9 shrink-0 pt-0.5">{grouped ? <div className="h-9 w-9" /> : profileButton}</div>

      <div className="min-w-0 flex-1">
        {!grouped && (
          <div className="mb-1 flex items-center gap-2 px-1">
            <button type="button" onClick={(e) => { e.stopPropagation(); onMention?.(m.username); }} disabled={deleted} data-testid={`public-username-${m.username}`} className="font-heading text-[13px] font-extrabold hover:opacity-80" data-yaprio-username-color={usernameColorStyle || undefined} style={usernameColorStyle ? { color: usernameColorStyle, "--yaprio-username-color": usernameColorStyle } : undefined}>
              @{m.username}
            </button>
            {Number(m.level || 0) >= 5 && (
              <span title="Level 5+ Chat Badge" className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[10px] font-bold leading-none text-orange-700">💬</span>
            )}
            {roleBadge && (
              <span
                data-testid={`public-role-badge-${m.username}`}
                title={roleBadge.label}
                className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-extrabold leading-none tracking-wide ${roleBadge.className}`}
              >
                <span aria-hidden="true">{roleBadge.icon}</span>{roleBadge.label}
              </span>
            )}
          </div>
        )}

        {m.reply_to && !deleted && (
          <button type="button" onClick={(e) => { e.stopPropagation(); onJumpToReply?.(m.reply_to); }} className="mb-1.5 ml-1 block max-w-xl rounded-xl border-l-2 border-pink-300 bg-pink-50/60 px-3 py-1.5 text-left text-xs text-slate-500 hover:bg-pink-50">
            <div className="font-semibold text-pink-500">@{m.reply_to.username}</div>
            <div className="truncate">{m.reply_to.deleted ? "Message deleted" : decodeText(m.reply_to.text)}</div>
          </button>
        )}

        <div className="flex min-w-0 max-w-full justify-start">
          <div
            data-testid="message-bubble"
            data-deleted={deleted ? "true" : "false"}
            className={`max-w-full whitespace-pre-wrap break-words [overflow-wrap:anywhere] rounded-2xl px-4 py-2 text-sm ${deleted ? "border border-slate-100 bg-slate-50 italic text-slate-400" : mine ? "rounded-tr-md border border-slate-200 bg-white text-slate-800" : "rounded-tl-md border border-[#ff812f] bg-[#ff812f] text-white"}`}
          >
            {deleted ? (
              <span data-translate="on" data-testid="public-message-deleted">{m.deleted_reason === "user_banned" ? "User has been banned by the Ancient Ones." : "Message deleted"}</span>
            ) : m.kind === "voice" && m.audio_url ? (
              <audio controls preload="metadata" src={mediaUrl(m.audio_url)} className="block max-w-[min(72vw,320px)]" aria-label={"Voice message from @" + m.username} />
            ) : m.kind === "image" && m.image_url ? (
              <img src={mediaUrl(m.image_url)} alt={"Image shared by @" + m.username} loading="lazy" className="block max-h-[360px] max-w-full rounded-xl object-contain" />
            ) : (
              renderText(decodeText(m.text))
            )}
          </div>
        </div>

        {!deleted && (
          <ReactionBar
            scope="public"
            messageId={m.id}
            reaction={reaction}
            onChange={setReaction}
            messageText={decodeText(m.text)}
            onDelete={mine ? () => onDelete(m.id) : undefined}
            onReport={!mine ? () => onReport(m) : undefined}
            onModerate={isStaff && !mine ? () => onModerate(m) : undefined}
            onReply={() => onReply(m)}
            onEdit={mine ? () => onEdit(m) : undefined}
          />
        )}

        {!deleted && (
          <div className="mt-1 flex items-center gap-2 px-1">
            {active && <span className="text-[10px] text-slate-400">{clockTime(m.created_at)}</span>}
            {m.edited_at && <span className="text-[10px] italic text-slate-400">edited</span>}
          </div>
        )}
      </div>
    </div>
  );
}
